import { useState, useRef, useEffect } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node) && isMobileMenuOpen) {
        const hamburger = document.getElementById('mobile-hamburger-btn');
        if (hamburger && !hamburger.contains(e.target as Node)) {
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <div className="flex items-center gap-6">
            <button
              onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
              className={`hidden md:flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'
              }`}
            >
              <i className="ri-home-4-line"></i>
              <span className="text-sm font-medium">Home</span>
            </button>

            <a
              href="/"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className={`flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-foreground-950' : 'text-white'
              }`}
            >
              <span className="font-heading text-lg md:text-xl font-bold tracking-tight">
                Itaobuy Spreadsheet
              </span>
            </a>
          </div>

          <div className="hidden md:flex items-center gap-5">
            <a href="#guida" onClick={(e) => { e.preventDefault(); document.getElementById('guida')?.scrollIntoView({ behavior: 'smooth' }); }}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'}`}>
              Guida
            </a>
            <div ref={dropdownRef} className="relative">
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'}`}>
                Categorie<i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] bg-white rounded-2xl py-4 px-3 shadow-lg border border-background-200">
                  <div className="grid grid-cols-2 gap-1.5">
                    {categories.map((cat) => (
                      <a key={cat.id} href={cat.url} target="_blank" rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-all cursor-pointer group"
                        onClick={() => setIsDropdownOpen(false)}>
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-accent-100 transition-colors flex-shrink-0">
                          <span className="text-xs font-bold text-foreground-400 group-hover:text-accent-600">{String(cat.id).padStart(2,'0')}</span>
                        </div>
                        <span>{cat.nameIt}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="#sicurezza" onClick={(e) => { e.preventDefault(); document.getElementById('sicurezza')?.scrollIntoView({ behavior: 'smooth' }); }}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'}`}>
              Sicurezza
            </a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); }}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'}`}>
              FAQ
            </a>
            <button onClick={() => { navigate('/articolo-guida-itaobuy-spreadsheet'); window.scrollTo(0, 0); }}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/85 hover:text-white'}`}>
              Blog
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => { navigate('/articolo-guida-itaobuy-spreadsheet'); window.scrollTo(0, 0); }}
              className={`hidden lg:flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                isScrolled ? 'bg-background-100 text-foreground-700 hover:bg-background-200' : 'bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20'
              }`}>
              <i className="ri-shopping-bag-3-line"></i>
              Guida Acquisti
            </button>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow"
              className={`hidden md:flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                isScrolled ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-white text-primary-600 hover:bg-white/90'
              }`}>
              itaobuy sheet<i className="ri-arrow-right-line"></i>
            </a>
            <button id="mobile-hamburger-btn" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? 'text-foreground-950' : 'text-white'}`}>
              <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden bg-white border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="px-5 py-4 space-y-1">
            <button onClick={() => { navigate('/'); window.scrollTo(0, 0); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer">
              <i className="ri-home-4-line text-lg text-accent-500"></i>Home
            </button>
            <button onClick={() => { navigate('/articolo-guida-itaobuy-spreadsheet'); window.scrollTo(0, 0); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer">
              <i className="ri-shopping-bag-3-line text-lg text-accent-500"></i>Guida Acquisti
            </button>
          </div>
          <div className="border-t border-background-200 px-5 py-3">
            <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-3 mb-2">Categorie Prodotto</p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a key={cat.id} href={cat.url} target="_blank" rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:bg-accent-50 hover:text-accent-700 transition-colors cursor-pointer"
                  onClick={() => setIsMobileMenuOpen(false)}>
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0"></span>{cat.nameIt}
                </a>
              ))}
            </div>
          </div>
          <div className="border-t border-background-200 px-5 py-3 space-y-1">
            <a href="#guida" onClick={(e) => { e.preventDefault(); document.getElementById('guida')?.scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">
              <i className="ri-guide-line text-accent-500"></i>Guida
            </a>
            <a href="#sicurezza" onClick={(e) => { e.preventDefault(); document.getElementById('sicurezza')?.scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">
              <i className="ri-shield-check-line text-accent-500"></i>Sicurezza
            </a>
            <a href="#faq" onClick={(e) => { e.preventDefault(); document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' }); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">
              <i className="ri-question-answer-line text-accent-500"></i>FAQ
            </a>
            <button onClick={() => { navigate('/articolo-guida-itaobuy-spreadsheet'); window.scrollTo(0, 0); setIsMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm text-foreground-600 hover:bg-background-100 cursor-pointer">
              <i className="ri-article-line text-accent-500"></i>Blog
            </button>
          </div>
          <div className="px-5 py-4 border-t border-background-200">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold cursor-pointer">itaobuy sheet<i className="ri-arrow-right-up-line"></i></a>
          </div>
        </div>
      )}
    </nav>
  );
}